import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { encryptField, decryptField } from '../lib/secureFields'
import { useCurrency } from '../hooks/useCurrency'
import { useLanguage } from '../hooks/useLanguage'
import { useToast } from './useToast'

const EMPTY_FORM = { amount: '', category: '', description: '' }

const inputStyle = {
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  fontSize: 14,
  outline: 'none',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  width: '100%',
  boxSizing: 'border-box'
}

function getTodayDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function PlantillasRapidas({ type, categorias, onAdded }) {
  const isIncome = type === 'income'
  const accent = isIncome ? '#22c55e' : '#ef4444'

  const { t } = useLanguage()
  const { format, currency, rates } = useCurrency()
  const toast = useToast()
  const rate = rates?.[currency] ?? 1

  const [open, setOpen] = useState(false)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState([])
  const [adding, setAdding] = useState(false)

  const [editingId, setEditingId] = useState(null) // null | 'new' | id
  const [form, setForm] = useState(EMPTY_FORM)
  const [customCategory, setCustomCategory] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  // ── Cargar plantillas ──
  async function fetchItems() {
    setLoading(true)
    const { data, error } = await supabase
      .from('plantillas_rapidas')
      .select('*')
      .eq('type', type)
      .order('created_at', { ascending: true })

    if (error) { setLoading(false); return }

    const dec = await Promise.all(
      data.map(async (p) => ({
        ...p,
        categoryEnc: p.category,
        descriptionEnc: p.description,
        category: p.category ? await decryptField(p.category) : '',
        description: p.description ? await decryptField(p.description) : ''
      }))
    )
    setItems(dec)
    setSelected(dec.map(p => p.id))
    setLoading(false)
  }

  useEffect(() => {
    if (open) fetchItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ── El tutorial controla el panel con este evento ──
  useEffect(() => {
    function onTour(e) {
      const action = e.detail
      if (action === 'open') {
        setOpen(true)
      } else if (action === 'close') {
        setOpen(false)
        cerrarForm()
      } else if (action === 'new') {
        setForm(EMPTY_FORM)
        setCustomCategory('')
        setError('')
        setEditingId('new')
      } else if (action === 'cancelForm') {
        cerrarForm()
      }
    }
    window.addEventListener('calypso:plantillas', onTour)
    return () => window.removeEventListener('calypso:plantillas', onTour)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Formulario ──
  function handleAmountChange(value) {
    const limpio = value.replace(/\D/g, '')
    const formateado = limpio ? new Intl.NumberFormat('es-CO').format(limpio) : ''
    setForm(prev => ({ ...prev, amount: formateado }))
  }

  function cerrarForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setCustomCategory('')
    setError('')
  }

  function empezarEditar(p) {
    const enLista = categorias.includes(p.category)
    setForm({
      amount: new Intl.NumberFormat('es-CO').format(Math.round(p.amount * rate)),
      category: enLista ? p.category : 'Otro',
      description: p.description || ''
    })
    setCustomCategory(enLista ? '' : p.category)
    setError('')
    setEditingId(p.id)
  }

  async function guardar() {
    setError('')
    const monto = Math.round(parseFloat(form.amount.replace(/\./g, '')) / rate)
    if (!form.amount || isNaN(monto) || monto <= 0) { setError(t('pl_err_monto')); return }

    const categoriaFinal = form.category === 'Otro' ? customCategory.trim() : form.category
    if (!categoriaFinal) { setError(t('pl_err_categoria')); return }
    if (categoriaFinal.length > 50) { setError(t('pl_err_categoria_larga')); return }
    if (form.description.trim().length > 200) { setError(t('pl_err_desc_larga')); return }

    setSaving(true)
    try {
      const categoryEnc = await encryptField(categoriaFinal)
      const descriptionEnc = form.description.trim()
        ? await encryptField(form.description.trim())
        : null

      let res
      if (editingId === 'new') {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) { setError(t('pl_err_guardar')); return }
        res = await supabase.from('plantillas_rapidas').insert([{
          user_id: user.id,
          type,
          amount: monto,
          category: categoryEnc,
          description: descriptionEnc
        }])
      } else {
        res = await supabase.from('plantillas_rapidas')
          .update({ amount: monto, category: categoryEnc, description: descriptionEnc })
          .eq('id', editingId)
      }

      <button
        type="button"
        onClick={() => {
          cerrarForm()
          window.dispatchEvent(new CustomEvent('calypso:plantillas-form-done'))
        }}
        style={{ ...smallBtn, flex: 1, padding: 10, borderRadius: 10, fontWeight: 600 }}
      >
        {t('cancelar')}
      </button>
      
    } catch {
      setError(t('pl_err_guardar'))
    } finally {
      setSaving(false)
    }
  }

  async function eliminar(id) {
    const { error } = await supabase.from('plantillas_rapidas').delete().eq('id', id)
    setConfirmDeleteId(null)
    if (!error) fetchItems()
  }

  // ── Agregar las seleccionadas a transactions ──
  function toggle(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const seleccionadas = items.filter(p => selected.includes(p.id))
  const total = seleccionadas.reduce((acc, p) => acc + p.amount, 0)

  async function agregarSeleccionadas() {
    if (seleccionadas.length === 0 || adding) return
    setAdding(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { setError(t('pl_err_agregar')); return }

      const hoy = getTodayDate()
      const registros = seleccionadas.map(p => ({
        user_id: user.id,
        type,
        amount: p.amount,
        category: p.categoryEnc,       // ya viene cifrado
        description: p.descriptionEnc, // ya viene cifrado
        date: hoy
      }))

      const { error } = await supabase.from('transactions').insert(registros)
      if (error) { setError(t('pl_err_agregar')); return }

      toast.success?.(`${t('pl_agregado_ok')} (${registros.length})`)
      setOpen(false)
      cerrarForm()
      onAdded?.()
    } catch {
      setError(t('pl_err_agregar'))
    } finally {
      setAdding(false)
    }
  }

  const smallBtn = {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    padding: '5px 9px',
    cursor: 'pointer',
    fontSize: 13,
    color: 'var(--color-text)'
  }

  return (
    <>
      {/* ── Botón flotante translúcido ── */}
      <button
        type="button"
        data-tour="mov-plantillas"
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 90,
          height: 52,
          padding: '0 18px',
          borderRadius: 999,
          background: 'rgba(128,128,128,0.18)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text)',
          fontWeight: 700,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          boxShadow: `0 6px 20px rgba(0,0,0,0.18), 0 0 0 1px ${accent}22`
        }}
      >
        <span style={{ fontSize: 18 }}>⚡</span>
        {t('pl_boton')}
      </button>

      {/* ── Panel ── */}
      {open && (
        <div
          onClick={() => { setOpen(false); cerrarForm() }}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
            zIndex: 150
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '20px 20px 0 0',
              width: '100%', maxWidth: 480,
              maxHeight: '88vh', overflowY: 'auto',
              padding: 18, boxSizing: 'border-box',
              display: 'flex', flexDirection: 'column', gap: 12
            }}
          >
            {/* Encabezado */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: accent }}>
                  ⚡ {t(isIncome ? 'pl_titulo_ingreso' : 'pl_titulo_gasto')}
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 3 }}>
                  {t(isIncome ? 'pl_desc_ingreso' : 'pl_desc_gasto')}
                </div>
              </div>
              <button type="button" onClick={() => { setOpen(false); cerrarForm() }} style={smallBtn}>✕</button>
            </div>

            {/* Formulario crear / editar */}
            {editingId ? (
              <div
                data-tour="pl-form"
                style={{
                  border: `1px solid ${accent}55`, background: 'var(--color-bg)',
                  borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 8
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {editingId === 'new' ? t('pl_nueva') : t('pl_editar')}
                </div>

                <input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('monto_placeholder')}
                  value={form.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  style={inputStyle}
                />

                <select
                  value={form.category}
                  onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="">{t('selecciona_categoria')}</option>
                  {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                </select>

                {form.category === 'Otro' && (
                  <input
                    type="text"
                    placeholder={t('titulo_categoria_placeholder')}
                    value={customCategory}
                    maxLength={50}
                    onChange={(e) => setCustomCategory(e.target.value)}
                    style={inputStyle}
                  />
                )}

                <input
                  type="text"
                  placeholder={t('descripcion_placeholder')}
                  value={form.description}
                  maxLength={200}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  style={inputStyle}
                />

                {error && <div style={{ fontSize: 12, color: '#f87171' }}>⚠️ {error}</div>}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={guardar}
                    disabled={saving}
                    style={{
                      flex: 1, padding: 10, borderRadius: 10, border: 'none',
                      background: 'var(--color-primary)', color: '#fff',
                      fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1
                    }}
                  >
                    {t('guardar_edicion')}
                  </button>
                  <button
                    type="button"
                    onClick={cerrarForm}
                    style={{ ...smallBtn, flex: 1, padding: 10, borderRadius: 10, fontWeight: 600 }}
                  >
                    {t('cancelar')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-tour="pl-nueva"
                onClick={() => { setForm(EMPTY_FORM); setCustomCategory(''); setError(''); setEditingId('new') }}
                style={{
                  padding: 10, borderRadius: 12, cursor: 'pointer',
                  border: `1px dashed ${accent}88`, background: `${accent}12`,
                  color: accent, fontWeight: 700, fontSize: 13
                }}
              >
                ＋ {t('pl_nueva')}
              </button>
            )}

            {/* Lista */}
            <div data-tour="pl-lista">
              {loading ? (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--color-muted)' }}>…</div>
              ) : items.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: 'var(--color-muted)' }}>
                  {t(isIncome ? 'pl_vacio_ingreso' : 'pl_vacio_gasto')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        padding: '10px 12px', borderRadius: 12,
                        border: '1px solid var(--color-border)',
                        background: selected.includes(p.id) ? `${accent}12` : 'var(--color-bg)',
                        transition: '0.2s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(p.id)}
                        onChange={() => toggle(p.id)}
                        style={{ accentColor: accent, width: 18, height: 18, flexShrink: 0, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 100 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{p.category}</div>
                        {p.description && (
                          <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{p.description}</div>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: accent }}>{format(p.amount)}</div>

                      {confirmDeleteId === p.id ? (
                        <div style={{ display: 'flex', gap: 6, width: '100%', justifyContent: 'flex-end' }}>
                          <button type="button" onClick={() => eliminar(p.id)}
                            style={{ ...smallBtn, background: '#ef4444', border: 'none', color: '#fff', fontWeight: 600 }}>
                            {t('si_eliminar')}
                          </button>
                          <button type="button" onClick={() => setConfirmDeleteId(null)} style={smallBtn}>
                            {t('cancelar')}
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" onClick={() => empezarEditar(p)} style={smallBtn}>✏️</button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(p.id)}
                            style={{ ...smallBtn, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)' }}
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Botón Agregar */}
            {items.length > 0 && !editingId && (
              <>
                {error && <div style={{ fontSize: 12, color: '#f87171' }}>⚠️ {error}</div>}
                <button
                  type="button"
                  data-tour="pl-agregar"
                  onClick={agregarSeleccionadas}
                  disabled={adding || seleccionadas.length === 0}
                  style={{
                    padding: 13, borderRadius: 12, border: 'none',
                    background: 'var(--color-primary)', color: '#fff',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    opacity: (adding || seleccionadas.length === 0) ? 0.6 : 1
                  }}
                >
                  ✅ {t('pl_agregar')} ({seleccionadas.length}) · {format(total)}
                </button>
                <div style={{ fontSize: 11, color: 'var(--color-muted)', textAlign: 'center' }}>
                  {t('pl_nota_fecha')}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}